import React, { useState, useCallback, memo, useEffect } from 'react';
import Icon from './AppIcon';
import Button from './ui/Button';
import Input from './ui/Input';
import useLanguage from '../hooks/useLanguage';
import { useToast } from './ui/Toast';

const EditPositionModal = memo(({ isOpen, onClose, position, onSave }) => {
  const [stopLoss, setStopLoss] = useState('');
  const [takeProfit, setTakeProfit] = useState('');
  const { t, isRTL } = useLanguage();
  const toast = useToast();

  useEffect(() => {
    if (position) {
      setStopLoss(position.stopLoss || position.sl || '');
      setTakeProfit(position.takeProfit || position.tp || '');
    }
  }, [position]);

  const handleSave = useCallback(async () => {
    try {
      const updatedPosition = {
        ...position,
        stopLoss: stopLoss ? parseFloat(stopLoss) : null,
        takeProfit: takeProfit ? parseFloat(takeProfit) : null,
        sl: stopLoss ? parseFloat(stopLoss) : null,
        tp: takeProfit ? parseFloat(takeProfit) : null,
      };

      // Call the save callback
      onSave(updatedPosition);

      // Show success toast
      toast.success(
        `${position.symbol} ${t('positionUpdated')}\nSL: ${stopLoss || '--'} | TP: ${takeProfit || '--'}`,
        4000
      );

      onClose();
    } catch (error) {
      console.error('Failed to update position:', error);
      toast.error(t('positionUpdateFailed'), 4000);
    }
  }, [position, stopLoss, takeProfit, onSave, onClose, toast, t]);

  if (!isOpen || !position) return null;

  const isBuy = position.type === 'buy' || position.type?.includes('buy');

  return (
    <div className={`fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 ${isRTL ? 'rtl' : ''}`}>
      <div className="bg-card rounded-lg shadow-lg w-full max-w-md">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="text-lg font-semibold text-foreground">
            {t('editPosition')} - {position.symbol}
          </h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-muted rounded"
          >
            <Icon name="X" size={20} className="text-muted-foreground" />
          </button>
        </div>

        {/* Modal Content */}
        <div className="p-4 space-y-4">
          {/* Position Info */}
          <div className="bg-muted p-3 rounded space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t('symbol')}:</span>
              <span className="font-medium">{position.symbol}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t('type')}:</span>
              <span className={`font-medium ${isBuy ? 'text-green-600' : 'text-red-600'}`}>
                {position.type?.toUpperCase()}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t('volume')}:</span>
              <span className="font-medium">{position.amount || position.volume} {t('lot')}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t('openPrice')}:</span>
              <span className="font-medium font-mono">{position.openPrice?.toFixed(5)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t('currentPrice')}:</span>
              <span className="font-medium font-mono">{position.currentPrice?.toFixed(5)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t('profit')}:</span>
              <span className={`font-medium ${position.profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {position.profit >= 0 ? '+' : ''}${position.profit?.toFixed(2)}
              </span>
            </div>
          </div>

          {/* Stop Loss */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">
              {t('stopLoss')} ({t('optional')})
            </label>
            <Input
              type="number"
              step="0.00001"
              value={stopLoss}
              onChange={(e) => setStopLoss(e?.target?.value)}
              placeholder="0.00000"
            />
          </div>

          {/* Take Profit */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">
              {t('takeProfit')} ({t('optional')})
            </label>
            <Input
              type="number"
              step="0.00001"
              value={takeProfit}
              onChange={(e) => setTakeProfit(e?.target?.value)}
              placeholder="0.00000"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-3 pt-2">
            <Button
              variant="outline"
              onClick={onClose}
              className="flex-1"
            >
              {t('cancel')}
            </Button>
            <Button
              onClick={handleSave}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
            >
              {t('save')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
});

EditPositionModal.displayName = 'EditPositionModal';

export default EditPositionModal;

